import {
  parseBlocks,
  pointListResponseSchema,
  waypointListResponseSchema,
  type PointDto,
  type RingBBox,
} from '@datum/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LogOut, Settings } from 'lucide-react'
import { lazy, Suspense, useCallback, useMemo, useState } from 'react'
import { api } from './lib/api'
import { getSession, listOrganizations, signOut } from './lib/auth'
import { getCachedPoints, putCachedPoints } from './lib/offline'
import { POINTS_QUERY_KEY, queuePoint } from './lib/sync'
import { useKeyboardNav } from './hooks/useKeyboardNav'
import { useSyncState } from './hooks/useSyncState'
import { AuthGate, CreateOrgCard } from './components/AuthGate'
import { DataPanel } from './components/DataPanel'
import { EditorPane } from './components/EditorPane'
import { PhaseBadge } from './components/SyncBadge'
import { SplitPane } from './components/SplitPane'
import { Timeline } from './components/Timeline'

// MapLibre is by far the heaviest dependency (~700 KB); code-split it so the
// auth/editor shell paints from the small entry chunk first.
const MapPanel = lazy(() => import('./components/MapPanel').then((m) => ({ default: m.MapPanel })))

function MapFallback() {
  return (
    <div className="mono flex h-full items-center justify-center bg-ink-950 text-[11px] tracking-[0.3em] text-zinc-600">
      MAP…
    </div>
  )
}

function bboxQuery(bbox: RingBBox | null): Record<string, string> {
  const query: Record<string, string> = { limit: '120' }
  if (bbox) {
    query['min_lat'] = bbox.minLat.toFixed(4)
    query['max_lat'] = bbox.maxLat.toFixed(4)
    query['min_lng'] = bbox.minLng.toFixed(4)
    query['max_lng'] = bbox.maxLng.toFixed(4)
  }
  return query
}

export function App() {
  const queryClient = useQueryClient()
  const sync = useSyncState()

  const sessionQuery = useQuery({ queryKey: ['session'], queryFn: getSession, retry: false })
  const user = sessionQuery.data ?? null

  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: listOrganizations,
    enabled: user !== null,
  })
  const orgs = useMemo(() => orgsQuery.data ?? [], [orgsQuery.data])
  const orgName = orgs.length > 0 ? (orgs[0]?.name ?? null) : null

  const [bbox, setBbox] = useState<RingBBox | null>(null)
  const [center, setCenter] = useState<[lng: number, lat: number] | null>(null)
  const bboxKey = useMemo(() => {
    if (!bbox) return 'world'
    return [bbox.minLat.toFixed(4), bbox.maxLat.toFixed(4), bbox.minLng.toFixed(4), bbox.maxLng.toFixed(4)].join('|')
  }, [bbox])

  const pointsQuery = useQuery({
    queryKey: [...POINTS_QUERY_KEY, bboxKey],
    enabled: user !== null && orgs.length > 0,
    queryFn: async (): Promise<PointDto[]> => {
      try {
        const response = await api.api.points.$get({ query: bboxQuery(bbox) })
        if (!response.ok) throw new Error(`points request failed (${response.status})`)
        const parsed = pointListResponseSchema.safeParse(await response.json())
        if (!parsed.success) throw new Error('points payload did not match the contract')
        void putCachedPoints(parsed.data.points)
        return parsed.data.points
      } catch (error) {
        if (!navigator.onLine) return getCachedPoints()
        throw error
      }
    },
  })

  const waypointsQuery = useQuery({
    queryKey: ['waypoints'],
    enabled: user !== null && orgs.length > 0,
    queryFn: async () => {
      const response = await api.api.waypoints.$get()
      if (!response.ok) throw new Error(`waypoints request failed (${response.status})`)
      const parsed = waypointListResponseSchema.safeParse(await response.json())
      if (!parsed.success) throw new Error('waypoints payload did not match the contract')
      return parsed.data.waypoints
    },
  })

  const points = useMemo(() => pointsQuery.data ?? [], [pointsQuery.data])
  const waypoints = useMemo(() => waypointsQuery.data ?? [], [waypointsQuery.data])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selectedPoint = useMemo(
    () => points.find((point) => point.id === selectedId && point.deleted_at === null) ?? null,
    [points, selectedId],
  )

  const [fitSignal, setFitSignal] = useState(0)
  const [helpOpen, setHelpOpen] = useState(false)
  const [dataOpen, setDataOpen] = useState(false)

  const patchPointsCache = useCallback(
    (next: PointDto) => {
      queryClient.setQueryData<PointDto[]>([...POINTS_QUERY_KEY, bboxKey], (current) => {
        const list = current ?? []
        const index = list.findIndex((point) => point.id === next.id)
        if (index === -1) return [next, ...list]
        const copy = [...list]
        copy[index] = next
        return copy
      })
    },
    [queryClient, bboxKey],
  )

  const handleSave = useCallback(
    (point: PointDto, edits: { title: string; content_raw: string; waypoint_id: string | null }) => {
      const next: PointDto = {
        ...point,
        title: edits.title,
        content_raw: edits.content_raw,
        content_json: JSON.stringify(parseBlocks(edits.content_raw)),
        waypoint_id: edits.waypoint_id,
        updated_at: Date.now(),
      }
      patchPointsCache(next)
      void queuePoint(next, point.version)
    },
    [patchPointsCache],
  )

  // Authoritative row from a direct mutation (AI): swap cache copies without
  // re-queueing — the server already holds this version.
  const adoptServerPoint = useCallback(
    (next: PointDto) => {
      patchPointsCache(next)
      void putCachedPoints([next])
    },
    [patchPointsCache],
  )

  const createDraft = useCallback(() => {
    if (!user || orgs.length === 0) return
    const now = Date.now()
    const [lng, lat] = center ?? [-122.4194, 37.7749]
    const draft: PointDto = {
      id: crypto.randomUUID(),
      org_id: orgs[0]?.id ?? '',
      user_id: user.id,
      title: 'Untitled note',
      content_raw: '',
      content_json: '[]',
      latitude: lat,
      longitude: lng,
      altitude: null,
      accuracy: null,
      waypoint_id: null,
      summary: null,
      tags: [],
      version: 1,
      created_at: now,
      updated_at: now,
      deleted_at: null,
    }
    patchPointsCache(draft)
    void queuePoint(draft, 1)
    setSelectedId(draft.id)
  }, [user, orgs, center, patchPointsCache])

  const moveSelection = useCallback(
    (delta: number) => {
      if (points.length === 0) return
      const index = selectedId === null ? -1 : points.findIndex((point) => point.id === selectedId)
      const nextIndex = index === -1 ? 0 : Math.min(points.length - 1, Math.max(0, index + delta))
      const target = points[nextIndex]
      if (target) setSelectedId(target.id)
    },
    [points, selectedId],
  )

  useKeyboardNav(
    useMemo(
      () => ({
        onNew: createDraft,
        onNext: () => moveSelection(1),
        onPrevious: () => moveSelection(-1),
        onFit: () => setFitSignal((signal) => signal + 1),
        onToggleHelp: () => setHelpOpen((open) => !open),
      }),
      [createDraft, moveSelection],
    ),
  )

  const handleSignOut = useCallback(() => {
    void signOut().then(() => queryClient.invalidateQueries({ queryKey: ['session'] }))
  }, [queryClient])

  if (sessionQuery.isLoading) {
    return (
      <div className="mono flex h-screen items-center justify-center bg-ink-950 text-[11px] tracking-[0.3em] text-zinc-600">
        DATUM…
      </div>
    )
  }

  if (user === null) return <AuthGate />
  if (orgsQuery.isLoading) return null
  if (orgs.length === 0) return <CreateOrgCard user={user} />

  const coordinateReadout = center !== null ? `${center[1].toFixed(5)}, ${center[0].toFixed(5)}` : '—'

  return (
    <div className="flex h-screen flex-col bg-ink-950">
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-edge bg-ink-900 px-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2">
            <span className="flex size-5 items-center justify-center rounded border border-edge bg-ink-800">
              <span className="size-1.5 rounded-full bg-sync" />
            </span>
            <span className="mono text-xs font-semibold tracking-[0.3em] text-zinc-100">DATUM</span>
          </span>
          {orgName !== null && (
            <span className="mono hidden rounded border border-edge bg-ink-850 px-2 py-0.5 text-[10px] text-zinc-400 sm:block">
              org: {orgName.toLowerCase().replaceAll(' ', '-')}
            </span>
          )}
        </div>

        <span className="mono hidden text-[11px] text-zinc-500 md:block">{coordinateReadout}</span>

        <div className="flex items-center gap-2">
          <PhaseBadge />
          <button
            type="button"
            onClick={() => setDataOpen(true)}
            className="rounded border border-edge bg-ink-850 p-1.5 text-zinc-500 hover:border-edge-strong hover:text-zinc-200"
            title="Data & account"
            aria-label="Data and account"
          >
            <Settings size={13} />
          </button>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="mono rounded border border-edge bg-ink-850 px-2 py-1 text-[10px] text-zinc-400 hover:border-edge-strong hover:text-zinc-200"
            title="Keyboard shortcuts (?)"
          >
            ?
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="rounded border border-edge bg-ink-850 p-1.5 text-zinc-500 hover:border-edge-strong hover:text-zinc-200"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={13} />
          </button>
        </div>
      </header>

      <SplitPane
        left={
          <>
            <Timeline
              points={points}
              waypoints={waypoints}
              selectedId={selectedId}
              loading={pointsQuery.isLoading}
              offline={!navigator.onLine}
              onSelect={setSelectedId}
              onNew={createDraft}
            />
            <div className="h-[46%] shrink-0 border-t border-edge bg-ink-900">
              <EditorPane
                point={selectedPoint}
                waypoints={waypoints}
                sync={sync}
                onSave={handleSave}
                onServerPoint={adoptServerPoint}
              />
            </div>
          </>
        }
        right={
          <Suspense fallback={<MapFallback />}>
            <MapPanel
              points={points}
              waypoints={waypoints}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onViewport={(nextBbox, nextCenter) => {
                setBbox(nextBbox)
                setCenter(nextCenter)
              }}
              fitSignal={fitSignal}
            />
          </Suspense>
        }
      />

      {helpOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-label="Keyboard shortcuts"
          onClick={() => setHelpOpen(false)}
        >
          <div className="w-full max-w-sm rounded-xl border border-edge-strong bg-ink-900 p-5" onClick={(event) => event.stopPropagation()}>
            <h3 className="mono mb-3 text-[11px] uppercase tracking-[0.2em] text-zinc-400">Shortcuts</h3>
            <ul className="flex flex-col gap-1.5 text-[13px] text-zinc-300">
              {[
                ['N', 'new note at map center'],
                ['J / K', 'next / previous note'],
                ['F', 'fit map to all notes'],
                ['Alt + ← / →', 'resize panes'],
                ['/', 'slash commands in the editor'],
                ['⌘/Ctrl + S', 'save now'],
                ['?', 'this help'],
                ['Esc', 'close'],
              ].map(([key, description]) => (
                <li key={key} className="flex items-baseline justify-between gap-4">
                  <kbd className="mono rounded border border-edge bg-ink-800 px-1.5 py-0.5 text-[11px] text-zinc-200">
                    {key}
                  </kbd>
                  <span className="text-right text-zinc-500">{description}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {dataOpen && <DataPanel onClose={() => setDataOpen(false)} email={user.email} />}
    </div>
  )
}
